import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { StopPropagationDirective } from '../../directives/stop-propagation.directive';
import { HxButton } from '../button/button';
import { HxIconButton } from '../icon-button/icon-button';
import { HxNote } from '../note/note';
import { HxSearchTypeAhead } from '../search-typeahead/search-typeahead';
import { type SearchItem, type SearchTypeaheadLoadMoreEvent } from '../search-typeahead/search-typeahead.types';
import { HxSideNav } from '../side-nav/side-nav';
import { HxSnackbarService } from '../snackbar/snackbar-service';
import { HxTooltipDirective } from '../tooltip/tooltip';
import { type IcdCode } from './icd-lookup.types';

/**
 * HxIcdLookup Component
 *
 * A self-contained control for searching and selecting ICD-10 diagnosis codes through a side-nav panel.
 *
 * @example
 *   ```typescript
 *   import { HxIcdLookup } from '@complexcare/harmony/icd-lookup';
 *
 *   <hx-icd-lookup
 *     [icdCodes]="availableCodes"
 *     [excludedCodes]="alreadyAddedCodes()"
 *     (codesAdded)="onCodesAdded($event)">
 *   </hx-icd-lookup>
 *   ```;
 *
 * @see specs/045-icd-lookup-control/spec.md
 */
@Component({
  selector: 'hx-icd-lookup',
  standalone: true,
  imports: [
    CommonModule,
    HxButton,
    HxIconButton,
    HxNote,
    HxSearchTypeAhead,
    HxSideNav,
    StopPropagationDirective,
    HxTooltipDirective,
  ],
  templateUrl: './icd-lookup.html',
  styleUrls: ['./icd-lookup.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hx-icd-lookup-control]': 'true',
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class HxIcdLookup implements OnDestroy {
  /** Reference to the side-nav panel for programmatic control */
  private readonly sideNav = viewChild<HxSideNav>(HxSideNav);

  /** Snackbar service for success notifications */
  private readonly snackbar = inject(HxSnackbarService);

  /** Is the ICD lookup panel currently open */
  protected readonly isPanelOpen = signal<boolean>(false);

  /** Array of available ICD codes to search from */
  readonly icdCodes = input.required<IcdCode[]>();

  /** Optional: ICD codes to exclude from search results (already added codes) */
  readonly excludedCodes = input<IcdCode[]>([]);

  /** Optional: Custom dialog title (default: "ICD Search") */
  readonly title = input<string>('ICD Search');

  /** Optional: Custom search placeholder (default: "Type in ICD code or code description to search") */
  readonly placeholder = input<string>('Type in ICD code or code description to search');

  /** Optional: Button aria-label (default: "Add ICD Codes") */
  readonly buttonAriaLabel = input<string>('Add ICD Codes');

  /** Optional: Icon for button (default: "plus-circle") */
  readonly buttonIcon = input<string>('plus-circle');

  /** Optional: Button variant (default: "fill") */
  readonly buttonVariant = input<'fill' | 'soft' | 'outline' | 'ghost'>('fill');

  /** Optional: Button context/color (default: "positive") */
  /** Optional: Button context/color (default: "brand") */
  readonly buttonContext = input<'brand' | 'neutral' | 'positive' | 'negative' | 'caution' | 'info'>('brand');

  /** Optional: Button size (default: "md") */
  readonly buttonSize = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');

  /** Optional: Whether button is rounded (default: true) */
  readonly buttonRounded = input<boolean>(true);

  /** Optional: Inner icon font-size (e.g. '1em', '20px') */
  readonly iconSize = input<string>('1em');

  /** Optional: Tooltip text for the button (default: undefined - no tooltip) */
  readonly buttonTooltip = input<string | undefined>(undefined);

  /** Optional: Whether the button is disabled (default: false) */
  readonly disabled = input<boolean>(false);

  /** Enables near-bottom incremental loading in search results. */
  readonly enableIncrementalLoading = input<boolean>(false);

  /** Whether more ICD results are available from the data source. */
  readonly hasMore = input<boolean>(false);

  /** Whether an incremental page is currently loading. */
  readonly loadingMore = input<boolean>(false);

  /** Page-size hint forwarded to load-more requests. */
  readonly pageSize = input<number>(10);

  /** Emits newly added codes when user confirms selection (fires on Add button click only) */
  readonly codesAdded = output<IcdCode[]>();

  /** Emits when the user types in the search box (for server-side search) */
  readonly searchChange = output<string>();

  /** Emits when the user scrolls to the end of results and needs the next page. */
  readonly loadMore = output<SearchTypeaheadLoadMoreEvent>();

  /** Available ICD codes for search (formatted for search-typeahead) */
  protected readonly searchItems = signal<SearchItem[]>([]);

  /** Currently selected ICD codes */
  protected readonly selectedCodes = signal<IcdCode[]>([]);

  /** Is "Add" button enabled (true when at least one code is selected) */
  protected readonly isAddButtonEnabled = computed(() => this.selectedCodes().length > 0);

  constructor() {
    // Update searchItems only when icdCodes or excludedCodes change (search results from parent).
    // isPanelOpen and selectedCodes are read with untracked so they don't re-trigger this effect;
    // re-triggering on isPanelOpen would repopulate stale results whenever the panel reopens.
    effect(
      () => {
        const icdCodes = this.icdCodes();
        const excluded = this.excludedCodes();

        if (!untracked(() => this.isPanelOpen())) return;

        const excludedSet = new Set(excluded.map((icd) => icd.code));
        const selectedSnapshot = untracked(() => this.selectedCodes());

        const items = icdCodes
          .filter((icd) => !excludedSet.has(icd.code))
          .map((icd) => ({
            id: icd.code,
            displayText: icd.displayText || (icd.description ? `${icd.code} - ${icd.description}` : icd.code),
            isSelected: selectedSnapshot.some((s) => s.code === icd.code),
            metadata: icd,
          }));

        this.searchItems.set(items);
      },
      { allowSignalWrites: true }
    );
  }

  /** Open the ICD lookup side-nav */
  protected openPanel(): void {
    // Prevent opening if disabled
    if (this.disabled()) {
      return;
    }

    this.selectedCodes.set([]); // Reset selections on open
    this.searchItems.set([]); // Clear stale results from previous session
    this.isPanelOpen.set(true);
    this.toggleBodyOpenClass(true);
  }

  /** Close the ICD lookup side-nav */
  protected closePanel(): void {
    this.isPanelOpen.set(false);
    this.searchItems.set([]);
    this.toggleBodyOpenClass(false);
  }

  ngOnDestroy(): void {
    this.toggleBodyOpenClass(false);
  }

  /**
   * Body class is used by global styles to hide underlying top notes and prevent layer conflicts while the ICD sidebar
   * is open.
   */
  private toggleBodyOpenClass(isOpen: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.classList.toggle('hx-icd-lookup-open', isOpen);
  }

  /** Handle selection changes from hx-search-typeahead */
  protected onSelectedItemsChanged(selectedItems: SearchItem[]): void {
    const codes = selectedItems.map((item) => item.metadata as IcdCode).filter((code): code is IcdCode => !!code);
    this.selectedCodes.set(codes);
  }

  /** Sync items state from hx-search-typeahead */
  protected onItemsChanged(updatedItems: SearchItem[]): void {
    this.searchItems.set(updatedItems);
  }

  /** Confirm selection and emit selected codes */
  protected onAdd(): void {
    const codes = this.selectedCodes();
    this.codesAdded.emit(codes);

    // Show success notification
    const count = codes.length;
    const message = count === 1 ? 'ICD code added successfully' : `${count} ICD codes added successfully`;
    this.snackbar.success(message);

    this.closePanel();
  }

  /** Cancel selection and close panel */
  protected onCancel(): void {
    this.closePanel();
  }

  /** Handle clicks on the overlay backdrop to close the panel */
  protected onOverlayClick(event: MouseEvent): void {
    // Only close if clicking directly on the overlay (not on panel content)
    if ((event.target as HTMLElement).classList.contains('hx-icd-lookup__overlay')) {
      this.closePanel();
    }
  }
}
